/** @internal */
export type ErrorStackEntry = {
    function: string | undefined
    file: string | undefined
    line: number | string | undefined
    column: number | string | undefined
}

/** @internal */
export type StackTransform = (stack: ErrorStackEntry[]) => ErrorStackEntry[]

function tryParseInt(value: number | string) {
    try {
        const parsed = parseInt(String(value))
        return isNaN(parsed) ? value : parsed
    } catch {
        return value
    }
}

const StackEntryEngine = {
    V8: {
        signature: /^\s*at\s+\S+\s+\(?\S+\)?$/,
        split: /:(?!\/)|\s+/,
    },
    SpiderMonkeyOrJSCore: {
        signature: /^[^@]*@\S*$/,
        split: /:(?!\/)|@+/,
    },
} as const

type IdentifiedStackTraceEntry = [string, keyof typeof StackEntryEngine | undefined]
type StackEntryEngineKey = keyof typeof StackEntryEngine

/** @internal */
export function parseErrorStack(errorStack: string | undefined, transforms?: StackTransform[]): ErrorStackEntry[] {
    if (!errorStack) {
        return []
    }

    let stack: ErrorStackEntry[] = errorStack
        /**
         * normalize nested stacks (Only effects V8 stack strings)
         *
         * eg:
         * "at eval (eval at <anonymous> (https://cdn.p-n.io/pushly-sdk.min.js?domain_key=UEk097ZMF07i4nVsRg79qKtoJD8Tcd2KE9gL&ver=6.4.5:1:49464), <anonymous>:1:4)"
         * is un-nested into the following two records:
         *   "at eval (<anonymous>:1:4)"
         *   "at <anonymous> (https://cdn.p-n.io/pushly-sdk.min.js?domain_key=UEk097ZMF07i4nVsRg79qKtoJD8Tcd2KE9gL&ver=6.4.5:1:49464)"
         */
        .replace(/(.*?\()(?:eval\s)?(at(?!.+at).*?)\),\s?(.*)/gm, '$1$3\n$2')
        // Gather individual trace lines
        .split(/[\n\r]\s*/)
        // Identify stack trace engine type
        .map<IdentifiedStackTraceEntry>((trace) => [
            trace,
            (Object.keys(StackEntryEngine) as StackEntryEngineKey[]).find((key) =>
                StackEntryEngine[key].signature.test(trace),
            ),
        ])
        // Filter out any entries with unmatched stack trace engine records
        // - includes initial Error type/name/description headers
        .filter(([, entryTypeKey]) => entryTypeKey)
        // Break individual traces into respective components
        .map(([trace, entryTypeKey]) =>
            trace
                .split(StackEntryEngine[entryTypeKey!].split)
                .filter((e) => e !== 'at')
                .map((e) => e.replace(/^\(|\)$/g, '')),
        )
        // Parse components and ensure numeric types
        .map<ErrorStackEntry>(([func, file, line, column]) => ({
            function: func || 'unknown',
            file: file || 'unknown',
            line: line === undefined ? 0 : tryParseInt(line),
            column: column === undefined ? 0 : tryParseInt(column),
        }))

    transforms?.forEach((transform) => {
        stack = transform(stack)
    })

    return stack
}
